import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnShare from 'containers/SocialShareModal/useOnShare';
import useFacebookAuthenticator from 'hooks/useFacebookAuthenticator';
import {
  scheduleFacebookVideo,
  shareFacebookVideo,
} from 'redux/modules/download';
import { facebookSelector, getFacebookPages } from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { SocialSharePlatformData } from '../types';
import {
  useFacebookPermissionStep,
  useFacebookPostComposerStep,
} from './steps';
import { FacebookSocialShareStep } from './types';

export default function useFacebookSocialShare(): SocialSharePlatformData<
  FacebookSocialShareStep
> {
  const { username, postStatus, pages, pagesLoading } = useSelector(
    facebookSelector,
  );

  const dispatch = useDispatch<Dispatch>();

  const {
    selectedAccount,
    description,
    onAuthSuccess,
    onAuthError,
  } = useSocialShareModal();

  const { authenticating, authenticate } = useFacebookAuthenticator({
    force: true,
    onAuthSuccess: () => {
      dispatch(getFacebookPages());
      onAuthSuccess();
    },
    onError: onAuthError,
  });

  const pageAccessToken = pages?.find(
    page => page.pageId === selectedAccount?.id,
  )?.pageAccessToken;

  const handleShare = useOnShare({
    shareNow: useCallback(
      () =>
        dispatch(
          shareFacebookVideo(pageAccessToken, selectedAccount?.id, description),
        ),
      [description, dispatch, pageAccessToken, selectedAccount?.id],
    ),
    schedule: useCallback(
      (scheduledDate: number) =>
        dispatch(
          scheduleFacebookVideo(
            pageAccessToken,
            selectedAccount?.id,
            description,
            scheduledDate,
          ),
        ),
      [description, dispatch, pageAccessToken, selectedAccount?.id],
    ),
  });

  const facebookPermissionStep = useFacebookPermissionStep();

  const facebookPostComposerStep = useFacebookPostComposerStep({
    onShare: handleShare,
  });

  return {
    platformAlias: 'facebook',
    authenticating: authenticating || pagesLoading || !username,
    accounts: pages.reduce((acc, page) => {
      acc.push({
        id: page.pageId,
        avatarSrc: page.imageUrl,
        username: page.name,
        handle: page.name,
      });

      return acc;
    }, []),
    steps: [facebookPermissionStep, facebookPostComposerStep],
    defaultStep: facebookPostComposerStep.id,
    postStatus,
    authenticate,
  };
}
